// ==========================================
// アプリケーション定数
// ==========================================

// API URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || (() => { throw new Error('REACT_APP_API_URL is not defined'); })();
export const ACCESSDB_API_BASE_URL = process.env.REACT_APP_ACCESSDB_API_URL || (() => { throw new Error('REACT_APP_ACCESSDB_API_URL is not defined'); })();
// TestChat WebSocket URL (accessCognito Outputs.TestChatWsEndpoint)
export const TESTCHAT_WS_URL = process.env.REACT_APP_TESTCHAT_WS_URL || '';
// Widget URL (chatbot front CDN)
export const WIDGET_URL = process.env.REACT_APP_WIDGET_URL || 'https://chat.entry-chat-bot.com';
export const DOGFOOD_API_KEY = process.env.REACT_APP_DOGFOOD_API_KEY || '';
// Chat API/WS origin (CSP connect-src用)
export const CHAT_API_ORIGIN = process.env.REACT_APP_CHAT_API_ORIGIN || '';
export const CHAT_WS_ORIGIN = process.env.REACT_APP_CHAT_WS_ORIGIN || '';

// 認証関連
export const TOKEN_STORAGE_KEY = 'entrybot_auth';
export const USER_STORAGE_KEY = 'entrybot_user';

// プラン情報 — rate_limit_per_min は BE shared/plan_policy.py PLAN_POLICY.rate_limit_per_min_cap と同値に保つこと(EB-732)
export const PLAN_INFO = {
  trial: {
    name: 'Trial',
    price_monthly: 0,
    price_yearly: 0,
    domains: 3,
    storage_mb: 50,
    monthly_messages: 300,
    chat_history_days: 30,
    rate_limit_per_min: 10,
    features: ['14日間無料', '月300メッセージ', '3BOT', '50MBストレージ', '30日間履歴保持'],
  },
  starter: {
    name: 'Starter',
    price_monthly: 4000,
    price_yearly: 30000,
    domains: 3,
    storage_mb: 200,
    monthly_messages: 300,
    chat_history_days: 30,
    rate_limit_per_min: 60,
    features: ['月300メッセージ', '3BOT', '200MBストレージ', '30日間履歴保持', 'カスタムロゴ'],
  },
  pro: {
    name: 'Pro',
    price_monthly: 10000,
    price_yearly: 100000,
    domains: 10,
    storage_mb: 1024,
    monthly_messages: 1000,
    chat_history_days: 90,
    rate_limit_per_min: 120,
    features: ['月1,000メッセージ', '10BOT', '1024MBストレージ', '90日間履歴保持', '詳細分析', '超過時自動延長'],
  },
  business: { // business price_monthly: 30000
    name: 'Business',
    price_monthly: 30000,
    price_yearly: 300000,
    domains: 30,
    storage_mb: 5120,
    monthly_messages: 10000,
    chat_history_days: 180,
    rate_limit_per_min: 240,
    features: ['Proのすべての機能', 'チャットボット 30個まで', '月1万メッセージ含有', 'ストレージ5GB', '履歴180日', '優先サポート'],
  },
} as const;

// ファイルアップロード制限
export const FILE_UPLOAD_LIMITS = {
  maxSize: 10 * 1024 * 1024, // 10MB
  maxSizeKB: 10240,
  allowedTypes: ['application/pdf', 'text/plain', 'text/markdown'],
  allowedExtensions: ['.pdf', '.txt', '.md'],
};

// BOT設定のデフォルト値
export const DEFAULT_BOT_SETTINGS = {
  primary_color: '#3B82F6',
  secondary_color: '#1F2937',
  logo_url: null,
  bot_name: 'EntryBOT',
  welcome_message: 'こんにちは！\n何かお手伝いできることはありますか？',
  placeholder: 'メッセージを入力...',
  suggested_questions: [],
  system_prompt: `あなたは親切で丁寧なカスタマーサポートアシスタントです。
提供された情報に基づいて、ユーザーの質問に正確に回答してください。
情報がない場合は、分からないと正直に伝えてください。
回答は簡潔に、日本語で行ってください。`,
  rate_limit: 10,
  max_message_length: 500,
  context_turns: 5,
};

// 業種別システムプロンプトテンプレート
export const SYSTEM_PROMPT_TEMPLATES = [
  {
    id: 'restaurant',
    label: '飲食店',
    prompt: `あなたは飲食店のカスタマーサポートアシスタントです。
営業時間・メニュー・予約・アクセス・アレルギー情報など、お客様のご質問に丁寧に回答してください。
予約のご案内は明確に行い、満席の場合は代替案をご提案ください。
回答は簡潔に、日本語で行ってください。`,
  },
  {
    id: 'clinic',
    label: 'クリニック',
    prompt: `あなたはクリニックの受付サポートアシスタントです。
診療科目・予約方法・受付時間・症状の目安などをご案内します。
※医療判断は行いません。具体的な症状については必ず医師にご相談ください。
回答は簡潔に、日本語で行ってください。`,
  },
  {
    id: 'realestate',
    label: '不動産',
    prompt: `あなたは不動産のカスタマーサポートアシスタントです。
物件情報・内覧予約・エリア案内・賃料・諸費用などのご質問に丁寧に回答してください。
内覧予約のご希望は担当者への連絡方法をご案内ください。
回答は簡潔に、日本語で行ってください。`,
  },
  {
    id: 'ec',
    label: 'ECサイト',
    prompt: `あなたはECサイトのカスタマーサポートアシスタントです。
商品検索・在庫状況・注文状況・配送方法・返品ポリシーなどのご質問に丁寧に回答してください。
返品・交換は規定に沿ってご案内し、個別の注文情報は担当部署へ誘導してください。
回答は簡潔に、日本語で行ってください。`,
  },
  {
    id: 'school',
    label: '学習塾',
    prompt: `あなたは学習塾のカスタマーサポートアシスタントです。
コース内容・料金・授業スケジュール・体験授業の予約などのご質問に丁寧に回答してください。
入塾をご検討の方には体験授業をご案内し、不安や疑問を丁寧に解消してください。
回答は簡潔に、日本語で行ってください。`,
  },
] as const;

// 日付フォーマット
export const DATE_FORMAT = {
  display: 'yyyy/MM/dd',
  displayWithTime: 'yyyy/MM/dd HH:mm',
  api: "yyyy-MM-dd'T'HH:mm:ss'Z'",
};

// ページネーション
export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// レート制限 — max は最上位plan(Pro)のrate_limit_per_min_cap に合わせる。実効上限はuserのplan別(DomainSettings参照・BE PLAN_POLICY enforceで担保)
export const RATE_LIMIT = {
  min: 1,
  max: 120,
  default: 10,
};

// メッセージ長制限
export const MESSAGE_LENGTH = {
  min: 100,
  max: 2000,
  default: 500,
};

// コンテキストターン制限
export const CONTEXT_TURNS = {
  min: 1,
  max: 20,
  default: 5,
};

// サイドバー幅
export const SIDEBAR_WIDTH = 280;
export const SIDEBAR_COLLAPSED_WIDTH = 72;
